/**
 * pageSeeding — a page's template sections are its BIRTH, not a state to
 * restore.
 *
 * PageNode used to seed whenever it mounted a page whose grid was empty. That
 * cannot tell "never seeded" from "emptied on purpose": delete every section
 * (by hand, or through builder-mcp) and the next load grew the template's Hero
 * back. So seeding is recorded on the page node as `seedSections: []` — the
 * same marker a deliberately blank page and a builder-mcp page already carry —
 * and an empty page with that marker stays empty.
 *
 * Pages created before this carry their template list (or nothing); they get
 * the marker on their next mount, seeded or not.
 */
import * as Y from "yjs";

/** Section template seeded into a page whose node names none. */
export const DEFAULT_SECTION = "hero";

/** The sitemap's page nodes (y-xyflow). */
const NODES = "nodes";

/** Origin of the marker write: not FLOW/GRID, so the UndoManager ignores it —
 *  undoing a user's edit must never resurrect a page's seed. */
export const PAGE_SEEDED_ORIGIN = Symbol("page-seeded");

/**
 * Seed `pageId` if its grid is empty and it has never been seeded, then mark
 * it seeded. `seed` inserts the given section templates (in order).
 */
export function seedPageOnce(
  ydoc: Y.Doc,
  pageId: string,
  gridIsEmpty: boolean,
  seed: (templates: readonly string[]) => void,
): void {
  const nodes = ydoc.getMap<unknown>(NODES);
  const node = nodes.get(pageId) as
    | { data?: Record<string, unknown> }
    | undefined;
  const raw = node?.data?.["seedSections"];
  const marked = Array.isArray(raw) && raw.length === 0;

  if (gridIsEmpty && !marked) {
    // Sanitize — don't trust arbitrary persisted node data.
    seed(
      Array.isArray(raw)
        ? raw.filter((s): s is string => typeof s === "string")
        : [DEFAULT_SECTION],
    );
  }
  if (node && !marked) {
    ydoc.transact(
      () =>
        nodes.set(pageId, {
          ...node,
          data: { ...node.data, seedSections: [] },
        }),
      PAGE_SEEDED_ORIGIN,
    );
  }
}
